import React, { useEffect, useState } from 'react';
import { Empty, Switch, Tooltip } from '@maxtropy/components';
import { CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import skyCamera from '../../assets/sky-camera.png';
import skyMonitorBg from '../../assets/sky-monitor-bg.png';
import { getCloudOption, getVisibleCloudOption } from '../../chartOptions';
import { SkyCamera } from '../../type';
import ChartBlock from '../ChartBlock';
import SectionPanel from '../SectionPanel';

interface SkyCameraPanelProps {
  skyCameras: SkyCamera[];
}

const SkyCameraPanel: React.FC<SkyCameraPanelProps> = ({ skyCameras }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselEnabled, setCarouselEnabled] = useState(true);
  const data = skyCameras[currentIndex];
  const hasMultipleCameras = skyCameras.length > 1;

  const changeCamera = (step: number) => {
    setCurrentIndex(index => (index + step + skyCameras.length) % skyCameras.length);
  };

  useEffect(() => {
    if (!carouselEnabled || !hasMultipleCameras) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentIndex(index => (index + 1) % skyCameras.length);
    }, 30000);

    return () => {
      window.clearInterval(timer);
    };
  }, [carouselEnabled, hasMultipleCameras, skyCameras.length]);

  useEffect(() => {
    if (currentIndex > skyCameras.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, skyCameras.length]);

  if (!data) {
    return (
      <SectionPanel title="天空成像仪监控" bodyClassName={styles.emptyBody}>
        <Empty description="暂无数据" />
      </SectionPanel>
    );
  }

  return (
    <SectionPanel
      title="天空成像仪监控"
      titleSuffix={
        <>
          <span>轮播:</span>
          <Switch checked={carouselEnabled} disabled={!hasMultipleCameras} onChange={setCarouselEnabled} />
        </>
      }
    >
      <div className={styles.skyDevice} style={{ backgroundImage: `url(${skyMonitorBg})` }}>
        <button
          type="button"
          className={styles.arrowButton}
          disabled={!hasMultipleCameras}
          onClick={() => changeCamera(-1)}
        >
          <LeftOutlined />
        </button>
        <div className={styles.skyDeviceContent} key={currentIndex}>
          <div className={styles.skyCameraIcon}>
            <img src={skyCamera} alt="" />
          </div>
          <div className={styles.deviceStatus}>
            <div className={styles.skyDeviceName}>{data.name}</div>
            {/* <div className={styles.onlineTag}>
              <span className={styles.onlineIcon}>
                <CheckOutlined />
              </span>
              <span>{data.status}</span>
            </div> */}
          </div>
          <div className={styles.skyMetrics}>
            {data.indicators.map(item => (
              <div className={styles.skyMetricCard} key={item.label}>
                <span className={styles.skyMetricLabel}>
                  {item.label}
                  <Tooltip title={item.commStatusText}>
                    <span className={`${styles.skyMetricDot} ${item.isCommAbnormal ? styles.skyMetricDotError : ''}`} />
                  </Tooltip>
                </span>
                <span className={styles.skyMetricValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          className={styles.arrowButton}
          disabled={!hasMultipleCameras}
          onClick={() => changeCamera(1)}
        >
          <RightOutlined />
        </button>
      </div>
      <div className={styles.twoCharts}>
        <ChartBlock title="综合云量" option={getCloudOption(data.totalCloud)} height={240} />
        <ChartBlock title="可见光云量" option={getVisibleCloudOption(data.visibleCloud)} height={240} />
      </div>
    </SectionPanel>
  );
};

export default SkyCameraPanel;
